import restaurantTableApi from '../../api/restaurant/restaurantTableApi';
import {
  CustomResponse,
  RestaurantTableDTO,
  RestaurantTableFilters,
  RestaurantTableForm,
  RestaurantTablePage,
  RestaurantTableStatus,
} from '../../types/restaurant/RestaurantTableTypes';

const payload = (form: RestaurantTableForm) => ({
  diningRoomId: form.diningRoomId === '' ? null : Number(form.diningRoomId),
  name: form.name.trim(),
  capacity: Number(form.capacity),
  status: form.status,
  shape: form.shape,
  positionX: form.positionX === '' ? null : Number(form.positionX),
  positionY: form.positionY === '' ? null : Number(form.positionY),
  displayOrder: Number(form.displayOrder),
  ...(form.id ? { active: form.active } : {}),
});

const RestaurantTableService = {
  listFiltered: async (branchId: number, filters: RestaurantTableFilters) => {
    const query = new URLSearchParams({
      page: String(filters.page),
      size: String(filters.size),
    });
    query.append('sort', 'displayOrder,asc');
    query.append('sort', 'name,asc');
    const response = await restaurantTableApi.addCustom(
      `/branches/${branchId}/filtered?${query.toString()}`,
      {
        search: filters.search?.trim() || null,
        diningRoomId: filters.diningRoomId,
        status: filters.status,
        active: filters.active,
      }
    );
    return response.data as RestaurantTablePage;
  },
  list: async (
    branchId: number,
    activeOnly = false,
    diningRoomId?: number
  ) => {
    const query = new URLSearchParams({ activeOnly: String(activeOnly) });
    if (diningRoomId) query.set('diningRoomId', String(diningRoomId));
    const response = await restaurantTableApi.fetchCustom(
      `/branches/${branchId}?${query.toString()}`
    );
    return response.data as CustomResponse<RestaurantTableDTO[]>;
  },
  /** Filtro real desde backend: salón + estado + búsqueda por código/nombre. */
  listForTransfer: async (
    branchId: number,
    filters: {
      diningRoomId?: number;
      status?: RestaurantTableStatus;
      search?: string;
    } = {}
  ) => {
    const query = new URLSearchParams({ activeOnly: 'true' });
    if (filters.diningRoomId)
      query.set('diningRoomId', String(filters.diningRoomId));
    if (filters.status) query.set('status', filters.status);
    if (filters.search?.trim()) query.set('search', filters.search.trim());
    const response = await restaurantTableApi.fetchCustom(
      `/branches/${branchId}?${query.toString()}`
    );
    return response.data as CustomResponse<RestaurantTableDTO[]>;
  },
  listByDiningRoom: async (
    branchId: number,
    diningRoomId: number,
    activeOnly = false
  ) => {
    const response = await restaurantTableApi.fetchCustom(
      `/branches/${branchId}/dining-rooms/${diningRoomId}?activeOnly=${activeOnly}`
    );
    return response.data as CustomResponse<RestaurantTableDTO[]>;
  },
  get: async (id: number) => {
    const response = await restaurantTableApi.fetchById(id);
    return response.data as CustomResponse<RestaurantTableDTO>;
  },
  create: async (branchId: number, form: RestaurantTableForm) => {
    const response = await restaurantTableApi.addCustom(
      `/branches/${branchId}`,
      payload(form)
    );
    return response.data as CustomResponse<RestaurantTableDTO>;
  },
  update: async (id: number, form: RestaurantTableForm) => {
    const response = await restaurantTableApi.modify(id, payload(form));
    return response.data as CustomResponse<RestaurantTableDTO>;
  },
  updateStatus: async (id: number, status: RestaurantTableStatus) => {
    const response = await restaurantTableApi.patchCustom(`${id}/status`, {
      status,
    });
    return response.data as CustomResponse<RestaurantTableDTO>;
  },
  updateActive: async (id: number, active: boolean) => {
    const response = await restaurantTableApi.patchCustom(`${id}/active`, {
      active,
    });
    return response.data as CustomResponse<RestaurantTableDTO>;
  },
};

export default RestaurantTableService;
